import React from "react";
import useSWR from "swr";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, FileText, FileSignature, ReceiptText, ShieldCheck, Building, FileType } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { Skeleton, StatusPill } from "@/components/Atoms";

const fetcher = (u) => api.get(u).then((r) => r.data);

const ICONS = {
  "Agreement": FileSignature,
  "Allotment Letter": FileText,
  "Demand Letter": FileText,
  "Receipt": ReceiptText,
  "NOC": ShieldCheck,
  "Sanction Plan": Building,
};

export default function BuyerDocuments() {
  const { data } = useSWR("/me/documents", fetcher);
  const nav = useNavigate();

  const requestDoc = async (type) => {
    try {
      await api.post("/tickets", { category: "Documentation", priority: "P3", subject: `Request: ${type}`, description: `Please share ${type} for our records.` });
      toast.success("Request submitted — we've created a ticket.");
      nav("/buyer/tickets");
    } catch { toast.error("Failed to submit request"); }
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="rc-title-1">My Documents</div>
        <div className="rc-callout">All papers related to your home, in one place.</div>
      </div>

      {!data && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{Array.from({length: 6}).map((_, i) => <Skeleton key={i} h={140}/>)}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {data?.map((d, i) => {
          const Icon = ICONS[d.document_type] || FileType;
          const available = d.status === "available" || !!d.document_url;
          return (
            <div key={d.document_id} className="rc-card rc-card-padded" data-testid={`doc-${i}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "rgba(15,75,127,0.10)", color: "var(--rc-blue)" }}>
                  <Icon size={22}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="rc-headline truncate">{d.document_type}</div>
                  <div className="rc-footnote">{available ? `Uploaded ${fmtDate(d.uploaded_at)}` : "Awaiting upload"}</div>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <StatusPill status={d.status || (available ? "available" : "pending")} />
                {available ? (
                  <button onClick={() => toast.success("Downloading…")} className="rc-btn rc-btn-sm rc-btn-secondary"><Download size={14}/> Download</button>
                ) : (
                  <button onClick={() => requestDoc(d.document_type)} className="rc-btn rc-btn-sm rc-btn-primary">Request</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
